import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FilterGroup, reduceFilterGroupToSelectedOptions } from '@app/components/filter/filter.component';
import {
    INTEGRATION_ACTIONS,
    IntegrationCardComponent,
} from '@app/components/integration-card/integration-card.component';
import { ButtonTypes } from '@app/components/platform/button/button.component';
import { ImportIntegration, ImportIntegrationCategory } from '@app/models/import/import-integration.model';
import { ImportIntegrationService } from '@app/modules/import/services';
import { PlatformModule } from '@app/platform.module';
import ConnectionStatus from '@app/types/connection-status.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { pick } from 'lodash-es';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil, takeWhile } from 'rxjs/operators';
import { INTEGRATIONS_HELP_LINK } from '../../integrations-import.view';

const IMPORT_INTEGRATION_ACTIONS = {
    ...INTEGRATION_ACTIONS,
    RECONNECT: 'import.integrations.steps.software.buttons.reconnect',
    DISCONNECT: 'import.integrations.steps.software.buttons.disconnect',
    IMPORT: 'import.integrations.steps.software.buttons.import',
    SUMMARY: 'import.integrations.steps.software.buttons.summary',
};

type IntegrationAction = keyof typeof IMPORT_INTEGRATION_ACTIONS;

const toUpperCaseWithoutWhiteSpace = (value: string): string => {
    return value.replace(/[\W_]+/g, '').toUpperCase();
};

const sortIntegrations = (integrations: ImportIntegration[]): ImportIntegration[] => {
    // If integrations are in the same state, sort by name alphabetically
    const compareAlpha = (a: ImportIntegration, b: ImportIntegration): number => {
        return a.name.localeCompare(b.name);
    };

    return integrations.sort((a, b) => {
        // Connected integrations always appear first
        if (a.status === 'connected') {
            return b.status === 'connected' ? compareAlpha(a, b) : -1;
        }
        // Disconnected integrations always appear last
        if (a.status === 'disconnected') {
            return b.status === 'disconnected' ? compareAlpha(a, b) : 1;
        }

        // Both are errored
        if (b.status === 'errored') {
            return compareAlpha(a, b);
        }

        return b.status === 'connected' ? 1 : -1;
    });
};

@Component({
    selector: 'app-integrations-choose-software',
    templateUrl: './integrations-choose-software.template.html',
    standalone: true,
    styleUrls: ['./integrations-choose-software.style.scss', '../integration-steps.scss'],
    imports: [PlatformModule, CommonModule, TranslateModule, IntegrationCardComponent],
})
export class IntegrationsChooseSoftwareStep implements OnDestroy {
    integrationsHelpLink = INTEGRATIONS_HELP_LINK;
    integrations: ImportIntegration[] = [];
    filteredIntegrations: ImportIntegration[] = [];
    isLoading = true;
    categoryFilter: FilterGroup<ImportIntegrationCategory> = { label: 'Categories', options: [] };
    categoryTranslations: { [key: string]: { label: { default: string; long?: string }; description: string } } = {};
    providerTranslations: Record<string, string> = {};
    searchTerm: string | null = null;
    activeFilters: ImportIntegrationCategory[] = [];

    PRIMARY_ACTIONS: Partial<Record<ConnectionStatus, { action: IntegrationAction; buttonVariant?: ButtonTypes }>> = {
        connected: { action: 'IMPORT', buttonVariant: 'outline' },
        errored: { action: 'RECONNECT', buttonVariant: 'outline' },
        disconnected: { action: 'CONNECT' },
    };

    _onDestroy$: Subject<void> = new Subject();

    constructor(
        public integrationService: ImportIntegrationService,
        private translateService: TranslateService
    ) {
        this.subscribeToTranslations();
        this.loadIntegrations();
    }

    async loadIntegrations(): Promise<void> {
        this.isLoading = true;
        const integrations = await this.integrationService.getIntegrations();
        // Sort by status and name
        const sortedIntegrations = sortIntegrations(integrations);
        // Set the base list of integrations
        this.integrations = sortedIntegrations;
        const categories = new Set<ImportIntegrationCategory>();
        // 100ms between adding each card to the DOM allows for smoother transition
        timer(0, 100)
            .pipe(
                takeWhile(() => this.filteredIntegrations.length !== sortedIntegrations.length),
                finalize(() => {
                    // Once all integrations are loaded, the categories within the filter can be determined
                    this.categoryFilter.options = [...categories].map((category) => ({
                        label: `import.integrations.categories.${category.toUpperCase()}.label.default`,
                        value: category,
                        selected: false,
                    }));
                    this.isLoading = false;
                })
            )
            .subscribe((index) => {
                // Set the list of integrations used for display
                this.filteredIntegrations.push(sortedIntegrations[index]);
                // Add categories to the Set to ensure there are no duplicates
                categories.add(sortedIntegrations[index].primaryCategory);
            });
    }

    // Ensures we have the proper translation values for both the integration categories and providers throughout any language changes
    subscribeToTranslations(): void {
        this.translateService
            .get('import.integrations.categories')
            .pipe(takeUntil(this._onDestroy$))
            .subscribe((categoryTranslations) => (this.categoryTranslations = categoryTranslations));
        // Provider names are needed so that the search can be performed on the translated strings
        this.translateService
            .get('import.integrations.providers')
            .pipe(takeUntil(this._onDestroy$))
            .subscribe((providerTranslations) => (this.providerTranslations = providerTranslations));
    }

    ngOnDestroy(): void {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }

    getAvailableActions({ status }: ImportIntegration): Partial<typeof IMPORT_INTEGRATION_ACTIONS> {
        const actions: IntegrationAction[] = [];

        if (status === 'connected') {
            actions.push('IMPORT');
        }
        if (status === 'errored') {
            actions.push('RECONNECT');
        }
        if (status === 'disconnected') {
            actions.push('CONNECT');
        }

        return pick(IMPORT_INTEGRATION_ACTIONS, actions);
    }

    handleSearch(searchTerm: string | null): void {
        const upperCaseSearch = toUpperCaseWithoutWhiteSpace(searchTerm ?? '');
        // If the search is identical to the last search (ie. only adding whitespace), don't re-run
        if (upperCaseSearch === this.searchTerm) {
            return;
        }
        this.searchTerm = toUpperCaseWithoutWhiteSpace(upperCaseSearch);
        this.filterIntegrations();
    }

    handleFilter(): void {
        this.activeFilters = reduceFilterGroupToSelectedOptions(this.categoryFilter).map((option) => option.value);
        this.filterIntegrations();
    }

    filterIntegrations(): void {
        this.isLoading = true;
        this.filteredIntegrations = this.integrations.filter((integration) => {
            // Check if there are any active filters first
            let match = this.activeFilters.length ? this.activeFilters.includes(integration.primaryCategory) : true;
            // Check if there is also a text search being performed
            if (match && this.searchTerm) {
                // All matches are checked against translation strings, not case sensitive and not including whitespace
                match = toUpperCaseWithoutWhiteSpace(
                    this.providerTranslations[integration.name.toUpperCase()]
                ).includes(this.searchTerm);
            }
            return match;
        });
        this.isLoading = false;
    }

    onIntegrationAction(action: IntegrationAction, integration: ImportIntegration): void {
        switch (action) {
            case 'CONNECT':
            case 'RECONNECT':
                this.integrationService.beginConnection(integration);
                break;
            case 'DISCONNECT':
                break;
            case 'IMPORT':
                this.integrationService.previewImport(integration);
                break;
        }
    }
}
